import { makeSelectors, makeStore } from './makeStore';

import { DateFormatter } from '../date/formatDate';
import { LangLanguageEnum } from '../generated/api/enumLib_api';
import type { PossibleLangCode } from '../language';
import { langLanguageEnumToPossibleLangCode } from '../language';

export type LangState<K extends PossibleLangCode> = {
  defaultLangCode: K;
  allowedLangCodes: K[];
  currentLangCode: K;
};

export function makeLangStore<
  T extends Partial<
    Record<
      PossibleLangCode,
      Record<
        string,
        string | ((...args: (string | number)[]) => string) | readonly string[]
      >
    >
  >,
  K extends PossibleLangCode,
>({
  initialDefaultLangCode,
  initialAllowedLangCodes,
  initialCurrentLangCode,
  langMap,
  onSetLang,
}: {
  langMap: T;
  /**
   * Must be included in `initialAllowedLangCodes`
   */
  initialDefaultLangCode: K;
  initialAllowedLangCodes: K[];
  initialCurrentLangCode: K | null;
  onSetLang?: (langCode: K) => void;
}) {
  const withDateFormatter = Object.fromEntries(
    Object.entries(langMap).map(([langCode, translations]) => [
      langCode,
      {
        ...translations,
        dateFormatter: new DateFormatter(langCode as PossibleLangCode),
      },
    ])
  ) as { [P in K]: T[P] & { dateFormatter: DateFormatter<K> } };

  const formatterMap = Object.fromEntries(
    initialAllowedLangCodes.map((langCode) => [
      langCode,
      new DateFormatter(langCode),
    ])
  ) as Record<K, DateFormatter<K>>;

  const store = makeStore<LangState<K>>(
    {
      defaultLangCode: initialDefaultLangCode,
      allowedLangCodes: initialAllowedLangCodes,
      currentLangCode:
        initialCurrentLangCode &&
        initialAllowedLangCodes.includes(initialCurrentLangCode)
          ? initialCurrentLangCode
          : initialDefaultLangCode,
    },
    undefined,
    (newState, prevState) => {
      if (newState.currentLangCode !== prevState.currentLangCode) {
        onSetLang?.(newState.currentLangCode);
      }
    }
  );

  const selectors = makeSelectors(store.getState());

  // Make sure any state changes goes through this function
  const setLangState = (partial: Partial<LangState<K>>) => {
    store.setState((state) => {
      const newState = { ...state, ...partial };
      if (!newState.allowedLangCodes.includes(newState.currentLangCode)) {
        newState.currentLangCode = newState.defaultLangCode;
      }
      // Make sure that both the default and current language codes are always allowed
      if (!newState.allowedLangCodes.includes(newState.defaultLangCode)) {
        // It would be an easy mistake to make, so we log an error in development
        if (import.meta.env.DEV) {
          console.error('Default language code not in allowed language codes');
        }
        return state;
      }
      return newState;
    });
  };

  const setCurrentLangCode = (currentLangCode: K) => {
    setLangState({ currentLangCode });
  };

  const setDefaultAndAllowedLanguages = (
    allowedLanguages: LangLanguageEnum[],
    defaultLangCode: string | null
  ) => {
    const allowedLangCodes = allowedLanguages
      .map((lang) => langLanguageEnumToPossibleLangCode[lang])
      .filter((code) => code in langMap) as K[];
    if (defaultLangCode) {
      if (defaultLangCode in langMap) {
        setLangState({
          defaultLangCode: defaultLangCode as K,
          allowedLangCodes,
        });
      }
    } else {
      setLangState({ allowedLangCodes });
    }
  };

  const useCurrentLangCode = (): K => store.useStore(selectors.currentLangCode);

  const useDefaultLangCode = (): K => store.useStore(selectors.defaultLangCode);

  const useAllowedLanguages = (): K[] =>
    store.useStore(selectors.allowedLangCodes);

  const useLang = (): T[K] & { dateFormatter: DateFormatter<K> } =>
    withDateFormatter[useCurrentLangCode()];

  const useFormatter = (): DateFormatter<K> =>
    formatterMap[useCurrentLangCode()];

  /**
   * NOT REACTIVE
   * Always prefer using {@link useFormatter} inside a component instead of this function
   */
  const getFormatter = (): DateFormatter<K> =>
    formatterMap[store.getState().currentLangCode];

  /**
   * NOT REACTIVE
   * Always prefer using {@link useLang} inside a component instead of this function
   */
  const getLang = (): T[K] => langMap[store.getState().currentLangCode];

  return {
    getLangState: store.getState,
    useDefaultLangCode,
    useAllowedLanguages,
    useCurrentLangCode,
    useLang,
    setLangState,
    setCurrentLangCode,
    setDefaultAndAllowedLanguages,
    useFormatter,
    getFormatter,
    getLang,
  };
}
